import { Modal, Button, DateInput, TextInput, Box } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { toDateOnlyPayload } from '../../../utils/dates';
import useTimeModules from '../../../hooks/useTimeModules';
import { TimeModule } from '../../../types/TimeModule';
import { FormProvider, useForm } from 'react-hook-form';
import { Container, Row, Col } from 'reactstrap';
import ClassroomLoader from '../../classesHistory/parts/ClassroomLoader';
import ClassroomSelector from '../../classesHistory/parts/ClassroomSelector';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (classroomId: number) => void;
  lessonId: number;
  date: string;
  schedule: string;
  disabled?: boolean;
  courseName?: string;
  currentClassroomCode: string;
  currentClassroomName: string;
}

interface FormData {
  date: string;
  classroom: { value: number; label: string; code?: string } | null;
  classroomCode?: string;
}

const DEFAULT_VALUES: FormData = {
  date: '',
  classroom: null,
  classroomCode: '',
};

export default function ChangeClassroomModal({
  isOpen,
  onClose,
  onConfirm,
  lessonId,
  date,
  schedule,
  disabled,
  courseName = '',
  currentClassroomCode: currentClassroomId,
  currentClassroomName,
}: Props) {
  const { t } = useTranslation();
  const { timeModules } = useTimeModules();
  const methods = useForm<FormData>({ defaultValues: DEFAULT_VALUES });
  const { handleSubmit, reset, watch, control, setValue } = methods;
  const classroom = watch('classroom');

  const prefilledDate = useMemo(() => toDateOnlyPayload(date), [date]);

  const { startModule, endModule } = useMemo(() => {
    const [startStr, endStr] = schedule.split('-').map((s) => s.trim());
    const start = timeModules.find(
      (tm: TimeModule) => tm.startTime.slice(0, 5) === startStr,
    );
    const end = timeModules.find(
      (tm: TimeModule) => tm.endTime.slice(0, 5) === endStr,
    );
    return { startModule: start, endModule: end };
  }, [schedule, timeModules]);

  useEffect(() => {
    if (isOpen) {
      reset(DEFAULT_VALUES);
      setValue('date', prefilledDate);
    }
  }, [isOpen, reset, setValue, prefilledDate]);

  const filters = useMemo(
    () => ({
      date: prefilledDate,
      startModuleId: startModule?.id,
      endModuleId: endModule?.id,
      lessonId,
    }),
    [prefilledDate, startModule?.id, endModule?.id, lessonId],
  );

  const submit = () => {
    if (classroom) {
      onConfirm(classroom.value);
    }
  };

  return (
    <Modal
      size="md"
      isOpen={isOpen}
      toggle={() => null}
      onClosed={() => {
        reset(DEFAULT_VALUES);
        onClose();
      }}
    >
      <p className="text-center text-dark fw-700 fs-22 mb-5">
        {t('startClasses.changeClassroom')}
      </p>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submit)}>
          <Container>
            <Row className="pb-3">
              <Col lg="6" className="px-0 pr-lg-3 pb-3">
                <Box
                  color="primary"
                  variant="outlined"
                  title={t('common.terms.course')}
                  body={courseName || '—'}
                  style={{ padding: '8px 15px', minWidth: '100%' }}
                />
              </Col>
              <Col lg="6" className="px-0 pb-3">
                <DateInput
                  label={t('common.terms.date')}
                  name="date"
                  control={control}
                  disabled
                />
              </Col>
            </Row>
            <Row className="pb-3">
              <Col lg="6" className="px-0 pr-lg-3 pb-3">
                <TextInput
                  label={t('classesHistory.startModule')}
                  name="startModuleDisplay"
                  value={
                    startModule
                      ? `${startModule.shortening} - ${startModule.name}`
                      : ''
                  }
                  disabled
                />
              </Col>
              <Col lg="6" className="px-0 pb-3">
                <TextInput
                  label={t('classesHistory.endModule')}
                  name="endModuleDisplay"
                  value={
                    endModule
                      ? `${endModule.shortening} - ${endModule.name}`
                      : ''
                  }
                  disabled
                />
              </Col>
            </Row>
            <Row className="pb-3">
              <Col lg="12" className="px-0 pb-3">
                {startModule && endModule ? (
                  <ClassroomLoader filters={filters}>
                    <ClassroomSelector
                      preselectId={currentClassroomId}
                      preselectName={currentClassroomName}
                    />
                  </ClassroomLoader>
                ) : (
                  <Box
                    color="primary"
                    variant="outlined"
                    title={t('common.terms.classroom')}
                    body={
                      t('classesHistory.errors.unableToDeriveModules') || '—'
                    }
                  />
                )}
              </Col>
            </Row>
            <Row className="mt-4">
              <Col lg="6" className="px-0 pr-lg-3 mb-3 mb-lg-0">
                <Button
                  fullwidth
                  onClick={onClose}
                  text={t('common.btnCancel')}
                  outlined
                  type="button"
                />
              </Col>
              <Col lg="6" className="px-0">
                <Button
                  fullwidth
                  type="submit"
                  text={t('common.btnConfirm')}
                  disabled={!classroom || disabled}
                />
              </Col>
            </Row>
          </Container>
        </form>
      </FormProvider>
    </Modal>
  );
}
